import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Badge, Card, Select, SelectItem, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import DateRangePresets from '../components/DateRangePresets';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import ReceivingDetailModal from '../components/ReceivingDetailModal';
import { useDrilldown } from '../components/DrilldownProvider';
import { fetchReceivings } from '../api/purchasing';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { Receiving, User } from '../api/types';

const fmtPct = new Intl.NumberFormat('es-MX', { style: 'percent', maximumFractionDigits: 0 });
const fmtQty = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

function completenessBadge(c: Receiving['completeness'], pct: number) {
  const color = c === 'full' ? 'emerald' : c === 'short' ? 'amber' : 'red';
  return <Badge color={color}>{fmtPct.format(pct)} · {t.purchasing.receptions.completeness[c]}</Badge>;
}

export default function PurchasingReceptions() {
  const user = useOutletContext<User>();
  const [from, setFrom] = useState(dateNDaysAgo(30));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [supId, setSupId] = useState('');
  const [completeness, setCompleteness] = useState<'all' | 'short' | 'full' | 'over'>('all');
  // Shared header store (lib/storeFilter); PurchasingLayout renders the picker.
  const [sid] = useStoreFilter(user);
  const [rows, setRows] = useState<Receiving[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<{ sid: number; onum: number; receiveNo: number } | null>(null);
  // Order detail comes from the app-wide drill-down stack (JCD-59); the
  // receiving modal below stays local (receive_no is not a drill kind).
  const { open: openDrill } = useDrilldown();
  const pg = usePagination(rows);

  const load = () => {
    setErr(null);
    setRows(null);
    fetchReceivings({ from, to, sid, sup_id: supId || undefined, completeness })
      .then(setRows)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [from, to, supId, completeness, sid]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Title>{t.purchasing.receptions.title}</Title>
          <Text className="text-tremor-content text-sm">{t.purchasing.receptions.subtitle}</Text>
        </div>
      </div>

      <Card className="space-y-3">
        <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">Proveedor (ID)</div>
            <TextInput value={supId} onValueChange={setSupId} placeholder="ABC" className="w-40" />
          </label>
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.purchasing.receptions.cols.pct}</div>
            <Select value={completeness} onValueChange={(v) => setCompleteness(v as typeof completeness)} className="w-40">
              <SelectItem value="all">{t.purchasing.receptions.completeness.all}</SelectItem>
              <SelectItem value="short">{t.purchasing.receptions.completeness.short}</SelectItem>
              <SelectItem value="full">{t.purchasing.receptions.completeness.full}</SelectItem>
              <SelectItem value="over">{t.purchasing.receptions.completeness.over}</SelectItem>
            </Select>
          </label>
          <div className="ml-auto text-sm text-tremor-content">
            {rows?.length ?? 0} recepciones
          </div>
        </div>
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && rows === null && <TableSkeleton rows={6} cols={8} />}
      {!err && rows !== null && rows.length === 0 && <EmptyState message={t.purchasing.receptions.empty} />}
      {!err && rows !== null && rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.purchasing.receptions.cols.date}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.receptions.cols.onum}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.receptions.cols.recvNo}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.receptions.cols.sup}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.receptions.cols.store}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.purchasing.receptions.cols.lines}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.purchasing.receptions.cols.qtyRecv}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.purchasing.receptions.cols.qtyOrd}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.receptions.cols.pct}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pg.pageRows.map((r) => (
                <TableRow
                  key={`${r.sid}-${r.onum}-${r.receive_no}`}
                  className="cursor-pointer hover:bg-tremor-background-muted"
                  onClick={() => setOpen({ sid: r.sid, onum: r.onum, receiveNo: r.receive_no })}
                >
                  <TableCell>{r.xfr_stk}</TableCell>
                  <TableCell>{r.onum}</TableCell>
                  <TableCell>{r.receive_no}</TableCell>
                  <TableCell>{r.sup_id} · {r.sup_name}</TableCell>
                  <TableCell>{r.sid}</TableCell>
                  <TableCell className="text-right">{r.lines}</TableCell>
                  <TableCell className="text-right">{fmtQty.format(r.qty_recv)}</TableCell>
                  <TableCell className="text-right">{fmtQty.format(r.po_qty_ord)}</TableCell>
                  <TableCell>{completenessBadge(r.completeness, r.completeness_pct)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination {...pg} className="px-4" />
        </Card>
      )}

      {open && (
        <ReceivingDetailModal
          sid={open.sid}
          onum={open.onum}
          receiveNo={open.receiveNo}
          onClose={() => setOpen(null)}
          onOpenPo={(s, o) => { setOpen(null); openDrill({ kind: 'order', sid: s, onum: o }); }}
        />
      )}
    </div>
  );
}
